import { useState } from 'react';
import { Box } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminTable from '../../shared/AdminTable';
import AdminStatusBadge from '../../shared/AdminStatusBadge';
import ErrorDetailModal from './ErrorDetailModal';
import { AdminError, AdminTableColumn, AdminTableAction } from '../../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface ErrorLogTableProps {
  errors: AdminError[];
  loading?: boolean;
  onResolve?: (errorId: string) => Promise<{ success: boolean }>;
}

export default function ErrorLogTable({ errors, loading, onResolve }: ErrorLogTableProps) {
  const [selectedError, setSelectedError] = useState<AdminError | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const columns: AdminTableColumn<AdminError>[] = [
    {
      key: 'severity',
      label: 'Severity',
      sortable: true,
      width: 100,
      render: (row) => <AdminStatusBadge status={row.severity} />,
    },
    {
      key: 'error_type',
      label: 'Type',
      sortable: true,
      width: 150,
    },
    {
      key: 'service',
      label: 'Service',
      sortable: true,
      width: 150,
    },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      render: (row) => (
        <Box sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.message}
        </Box>
      ),
    },
    {
      key: 'occurrence_count',
      label: 'Count',
      sortable: true,
      width: 80,
    },
    {
      key: 'last_occurred_at',
      label: 'Last Occurred',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.last_occurred_at), { addSuffix: true }),
    },
    {
      key: 'resolved',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (row) => <AdminStatusBadge status={row.resolved ? 'active' : 'flagged'} />,
    },
  ];

  const actions: AdminTableAction<AdminError>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => {
        setSelectedError(row);
        setDetailModalOpen(true);
      },
      color: 'primary',
    },
    {
      label: 'Mark Resolved',
      icon: <CheckCircleIcon />,
      onClick: async (row) => {
        if (onResolve) {
          await onResolve(row.id);
        }
      },
      color: 'success',
      disabled: (row) => row.resolved,
    },
  ];

  return (
    <>
      <AdminTable columns={columns} data={errors} actions={actions} isLoading={loading} />

      {selectedError && (
        <ErrorDetailModal
          open={detailModalOpen}
          error={selectedError}
          onClose={() => setDetailModalOpen(false)}
          onResolve={onResolve}
        />
      )}
    </>
  );
}
